import { useCallback, useEffect, useRef, useState } from 'react';

import {
  notificationApi,
  NotificationStatusFilter,
  useNotificationServiceContext,
  useNotificationSubscription,
  NotificationApplication,
  UserNotification,
} from '@/core/notifications/export';

export const PIPELINE_PUBLISHER_NAMES = ['Pipeline alerts', 'Pipeline notifications'];

function findPipelinePublisher(applications: NotificationApplication[]) {
  const byName = new Map(applications.map(app => [app.name.trim().toLowerCase(), app]));
  for (const name of PIPELINE_PUBLISHER_NAMES) {
    const match = byName.get(name.toLowerCase());
    if (match) return match;
  }
  return undefined;
}

export const PAGE_SIZES = [10, 20, 50];
const DEFAULT_PAGE_SIZE = 20;
const UNREAD_COUNT_CAP = 99;

export interface UsePipelineNotificationsResult {
  notifications: UserNotification[];
  loading: boolean;
  unreadCount: number;
  publisherId?: string;
  publisherName?: string;
  publisherMissing: boolean;

  page: number;
  setPage: (page: number) => void;
  hasMore: boolean;
  pageSize: number;
  setPageSize: (size: number) => void;

  statusFilter: NotificationStatusFilter;
  setStatusFilter: (status: NotificationStatusFilter) => void;
  selectedSubscription?: string;
  setSelectedSubscription: (id?: string) => void;

  selectedIds: Set<string>;
  toggleSelected: (id: string) => void;
  toggleSelectAll: () => void;
  allSelected: boolean;

  markRead: (id: string) => Promise<void>;
  markUnread: (id: string) => Promise<void>;
  markSelectedRead: () => Promise<void>;
}

export function usePipelineNotifications(): UsePipelineNotificationsResult {
  const { applications, isLoading: catalogLoading } = useNotificationServiceContext();
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPageState] = useState(0);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);
  const [statusFilter, setStatusFilterState] = useState<NotificationStatusFilter>('all');
  const [selectedSubscription, setSelectedSubscriptionState] = useState<string>();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const cursorsRef = useRef<(string | undefined)[]>([undefined]);

  const publisher = findPipelinePublisher(applications);
  const publisherId = publisher?.id;

  const resetPaging = useCallback(() => {
    cursorsRef.current = [undefined];
    setPageState(0);
  }, []);

  const setPage = useCallback((next: number) => {
    if (next < 0 || next >= cursorsRef.current.length) return;
    setPageState(next);
  }, []);

  const setPageSize = useCallback(
    (size: number) => {
      resetPaging();
      setPageSizeState(size);
    },
    [resetPaging],
  );

  const setStatusFilter = useCallback(
    (status: NotificationStatusFilter) => {
      resetPaging();
      setStatusFilterState(status);
    },
    [resetPaging],
  );

  const setSelectedSubscription = useCallback(
    (id?: string) => {
      resetPaging();
      setSelectedSubscriptionState(id);
    },
    [resetPaging],
  );

  const refreshUnreadCount = useCallback(async () => {
    if (!publisherId) return;
    try {
      const result = await notificationApi.getNotifications({
        publisher: publisherId,
        status: 'unread',
        limit: UNREAD_COUNT_CAP,
      });
      setUnreadCount(Math.min(result.items.length, UNREAD_COUNT_CAP));
    } catch {}
  }, [publisherId]);

  const loadPage = useCallback(async () => {
    if (!publisherId) return;
    setLoading(true);
    setSelectedIds(new Set());
    try {
      const result = await notificationApi.getNotifications({
        limit: pageSize,
        cursor: cursorsRef.current[page],
        publisher: publisherId,
        subscription: selectedSubscription,
        status: statusFilter === 'all' ? undefined : statusFilter,
      });

      const items = result.items;
      const nextCursor =
        result.nextCursor ?? (items.length === pageSize ? items[items.length - 1]?.id : undefined);

      setNotifications(items);
      if (nextCursor) cursorsRef.current[page + 1] = nextCursor;
      else cursorsRef.current.length = page + 1;
      setHasMore(!!nextCursor);
    } finally {
      setLoading(false);
    }
  }, [publisherId, page, pageSize, selectedSubscription, statusFilter]);

  useEffect(() => {
    if (catalogLoading) return;
    if (!publisherId) {
      setNotifications([]);
      cursorsRef.current = [undefined];
      setHasMore(false);
      setLoading(false);
      return;
    }
    void loadPage();
  }, [catalogLoading, publisherId, loadPage]);

  useEffect(() => {
    void refreshUnreadCount();
  }, [refreshUnreadCount]);

  useNotificationSubscription(() => {
    if (page === 0) void loadPage();
    void refreshUnreadCount();
  });

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allSelected =
    notifications.length > 0 && notifications.every(n => selectedIds.has(n.notification.id));

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      const ids = notifications.map(n => n.notification.id);
      const all = ids.length > 0 && ids.every(id => prev.has(id));
      return all ? new Set<string>() : new Set(ids);
    });
  }, [notifications]);

  const setReadAt = useCallback((id: string, readAt: string | null) => {
    setNotifications(prev => prev.map(n => (n.notification.id === id ? { ...n, readAt } : n)));
  }, []);

  const markRead = useCallback(
    async (id: string) => {
      setReadAt(id, new Date().toISOString());
      try {
        await notificationApi.ackNotification(id);
      } catch {
        setReadAt(id, null);
      }
      void refreshUnreadCount();
    },
    [setReadAt, refreshUnreadCount],
  );

  const markUnread = useCallback(
    async (id: string) => {
      setReadAt(id, null);
      try {
        await notificationApi.unackNotification(id);
      } catch {
        setReadAt(id, new Date().toISOString());
      }
      void refreshUnreadCount();
    },
    [setReadAt, refreshUnreadCount],
  );

  const markSelectedRead = useCallback(async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const now = new Date().toISOString();
    setNotifications(prev =>
      prev.map(n => (ids.includes(n.notification.id) ? { ...n, readAt: n.readAt ?? now } : n)),
    );
    setSelectedIds(new Set());
    await Promise.all(ids.map(id => notificationApi.ackNotification(id).catch(() => {})));
    void refreshUnreadCount();
  }, [selectedIds, refreshUnreadCount]);

  return {
    notifications,
    loading: loading || catalogLoading,
    unreadCount,
    publisherId,
    publisherName: publisher?.name,
    publisherMissing: !catalogLoading && !publisherId,
    page,
    setPage,
    hasMore,
    pageSize,
    setPageSize,
    statusFilter,
    setStatusFilter,
    selectedSubscription,
    setSelectedSubscription,
    selectedIds,
    toggleSelected,
    toggleSelectAll,
    allSelected,
    markRead,
    markUnread,
    markSelectedRead,
  };
}
